import { ref } from 'vue'
import type { Ref } from 'vue'
import { isAxiosError } from 'axios'
import { createPayoutHostedLink, refreshPayoutAccount } from '@/api/payouts'
import { useAuth } from '@/composables/useAuth'
import type { PayoutAccount, PayoutLinkPurpose } from '@/types/payouts'

export type PayoutAccountErrorKind = 'provider' | 'forbidden' | 'generic'

function classify(e: unknown): PayoutAccountErrorKind {
  if (isAxiosError(e)) {
    const status = e.response?.status
    if (status === 403) return 'forbidden'
    if (status === 502 || status === 429) return 'provider'
  }
  return 'generic'
}

// Guards the automatic "refresh link" redirect so a failing link can never
// loop the owner between the app and the provider (once per page load).
let refreshRedirectAttempted = false

export function resetRefreshRedirectGuard(): void {
  refreshRedirectAttempted = false
}

export function claimRefreshRedirect(): boolean {
  if (refreshRedirectAttempted) return false
  refreshRedirectAttempted = true
  return true
}

export function usePayoutAccount(orgId: Ref<number> | number) {
  const { user } = useAuth()

  const refreshing = ref(false)
  const redirecting = ref(false)
  const error = ref<PayoutAccountErrorKind | null>(null)

  function id(): number {
    return typeof orgId === 'number' ? orgId : orgId.value
  }

  /** Writes a fresh payout account state into the matching user.organisations entry. */
  function writeBack(account: PayoutAccount): void {
    if (user.value === null) return
    user.value.organisations = user.value.organisations.map((o) =>
      o.id === id() ? { ...o, payout_account: account } : o,
    )
  }

  async function refresh(): Promise<PayoutAccount | null> {
    refreshing.value = true
    error.value = null
    try {
      const account = await refreshPayoutAccount(id())
      writeBack(account)
      return account
    } catch (e: unknown) {
      error.value = classify(e)
      return null
    } finally {
      refreshing.value = false
    }
  }

  /** Requests a single-use hosted link and redirects the browser to it. */
  async function startHostedFlow(purpose: PayoutLinkPurpose): Promise<boolean> {
    redirecting.value = true
    error.value = null
    try {
      const link = await createPayoutHostedLink(id(), purpose)
      window.location.assign(link.url)
      return true
    } catch (e: unknown) {
      error.value = classify(e)
      redirecting.value = false
      return false
    }
  }

  return { refreshing, redirecting, error, refresh, startHostedFlow }
}
